import { useSyncExternalStore } from "react"

// Phrase's in-context editor keeps its login here and fires no event when it changes.
const TOKEN_KEY = "App::token"
export const TOKEN_POLL_INTERVAL_MS = 1000

const listeners = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | undefined

/**
 * Registers a `useSyncExternalStore` listener and polls the token for it, sharing
 * one interval across every component, since Phrase fires no event on login or logout.
 *
 * @param listener re-reads the snapshot on every tick
 * @returns unsubscribes the listener, clearing the interval once none are left
 */
const subscribe = (listener: () => void) => {
  listeners.add(listener)
  timer ??= setInterval(() => listeners.forEach((notify) => notify()), TOKEN_POLL_INTERVAL_MS)

  return () => {
    listeners.delete(listener)
    if (listeners.size) return
    clearInterval(timer)
    timer = undefined
  }
}

/**
 * Reads whether Phrase holds a token. Phrase stores an empty string before login,
 * which counts as logged out.
 *
 * @returns false as well when localStorage is unavailable, e.g. blocked site data
 */
const getSnapshot = () => {
  try {
    return !!localStorage.getItem(TOKEN_KEY)
  } catch {
    return false
  }
}

/**
 * Snapshot for the server, for hydration, and for pages without the editor, so
 * they render real copy.
 */
const alwaysFalse = () => false

/**
 * Subscription for pages without the editor, which never poll.
 *
 * @returns a no-op unsubscribe
 */
const subscribeNowhere = () => () => {}

/**
 * Whether someone is logged in to Phrase's in-context editor in this browser.
 * Always false on the server, during hydration, and while `enabled` is false.
 *
 * @param enabled whether the editor is on for this page, the token is only polled then
 */
export const usePhraseIceSignedIn = (enabled: boolean) =>
  useSyncExternalStore(
    enabled ? subscribe : subscribeNowhere,
    enabled ? getSnapshot : alwaysFalse,
    alwaysFalse,
  )
